import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Globe, Plus, Trash2, Loader2, Pencil } from 'lucide-react';

const BLANK = { country_code: '', country_name: '', currency: '', symbol: '', t180_regular: '', t180_promo: '', t365_regular: '', t365_promo: '', promo_enabled: false, active: true };

export default function AdminCountryPricing() {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get('/admin/country-pricing').then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id, name) => {
    if (!window.confirm(`Remove pricing for ${name}?`)) return;
    await api.delete(`/admin/country-pricing/${id}`); toast.success('Removed'); load();
  };

  return (
    <div className="space-y-5" data-testid="admin-pricing">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-mini text-[#00C8FF]">Monetization</div>
          <h1 className="font-display font-black text-3xl mt-1 flex items-center gap-2"><Globe className="w-7 h-7 text-[#00C8FF]" /> Country Pricing</h1>
          <p className="text-sm text-muted-foreground mt-1">Independent regional prices for Transform 180 / 365. Changes apply instantly — no app update needed.</p>
        </div>
        <Button onClick={() => setEdit({ ...BLANK, _new: true })} data-testid="add-country-btn" className="bg-[#00C8FF] text-[#081726] font-semibold"><Plus className="w-4 h-4 mr-1" /> Add Country</Button>
      </div>

      {!rows ? <div className="text-muted-foreground py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
        <div className="space-y-3">
          {rows.map(r => (
            <div key={r.id} className="regen-card-elevated p-4" data-testid={`pricing-${r.country_code}`}>
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold flex items-center gap-2">{r.country_name} <span className="text-[11px] text-muted-foreground">({r.country_code} · {r.currency})</span>
                    {r.promo_enabled && <span className="pill bg-[#FF6FA8]/15 text-[#FF6FA8] text-[10px]">Promo on</span>}
                    {!r.active && <span className="pill bg-[var(--c-film)] text-muted-foreground text-[10px]">Inactive</span>}
                  </div>
                  <div className="text-[12px] text-muted-foreground mt-1">
                    T180: {r.symbol}{r.t180_promo || r.t180_regular} {r.promo_enabled && <span className="line-through">{r.symbol}{r.t180_regular}</span>} · T365: {r.symbol}{r.t365_promo || r.t365_regular} {r.promo_enabled && <span className="line-through">{r.symbol}{r.t365_regular}</span>}
                  </div>
                </div>
                <Button size="icon" variant="outline" className="border-[var(--c-hair)]" onClick={() => setEdit(r)} data-testid={`edit-${r.country_code}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" className="text-red-400" onClick={() => del(r.id, r.country_name)} data-testid={`del-${r.country_code}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {edit && <EditDialog row={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}

      <PromoManager />
    </div>
  );
}

function PromoManager() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get('/promo-banner').then(r => setP(r.data)); }, []);
  if (!p) return null;
  const f = (k, v) => setP(s => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.put('/admin/promo-banner', { enabled: p.enabled, title: p.title, subtitle: p.subtitle, badge: p.badge, ends_text: p.ends_text, color: p.color });
      toast.success('Promo banner saved');
    } catch { toast.error('Save failed'); } finally { setBusy(false); }
  };
  const txt = (k, label, ph) => (<div><Label>{label}</Label><Input value={p[k] || ''} placeholder={ph} onChange={e => f(k, e.target.value)} className="bg-transparent border-[var(--c-hair)]" data-testid={`promo-${k}`} /></div>);
  return (
    <div className="regen-card-elevated p-5 space-y-3 max-w-2xl mt-6" data-testid="promo-manager">
      <div className="flex items-center gap-3">
        <div className="flex-1"><div className="font-display font-bold text-lg">Launch Promo Banner</div><div className="text-xs text-muted-foreground">Shown on the YourReGen Protocol enrollment page. Toggle anytime — no redeploy.</div></div>
        <Switch checked={!!p.enabled} onCheckedChange={v => f('enabled', v)} data-testid="promo-enabled" />
      </div>
      {txt('title', 'Title', '🎉 Launch Offer — 50% Off')}
      {txt('subtitle', 'Subtitle', 'Save 40% today')}
      <div className="grid grid-cols-2 gap-3">{txt('badge', 'Badge', 'LIMITED TIME')}{txt('ends_text', 'Ends text', 'Ends Sunday')}</div>
      <Button onClick={save} disabled={busy} className="w-full bg-[var(--c-green)] text-[#081726] font-semibold" data-testid="promo-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Promo Banner'}</Button>
    </div>
  );
}

function EditDialog({ row, onClose, onSaved }) {
  const [f, setF] = useState(row);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const num = (k) => ['t180_regular', 't180_promo', 't365_regular', 't365_promo'].includes(k);
  const save = async () => {
    setBusy(true);
    const payload = { ...f };
    ['t180_regular', 't180_promo', 't365_regular', 't365_promo'].forEach(k => payload[k] = parseFloat(payload[k]) || 0);
    delete payload._new; delete payload.id; delete payload._id; delete payload.updated_at;
    try {
      if (row._new) await api.post('/admin/country-pricing', payload);
      else await api.put(`/admin/country-pricing/${row.id}`, payload);
      toast.success('Saved'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); setBusy(false); }
  };
  const fld = (k, label, ph) => (<div><Label>{label}</Label><Input type={num(k) ? 'number' : 'text'} value={f[k] ?? ''} placeholder={ph} onChange={e => set(k, e.target.value)} className="bg-transparent border-[var(--c-hair)]" data-testid={`cp-${k}`} /></div>);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-regen-card border-[var(--c-hair)] max-h-[88vh] overflow-y-auto" data-testid="pricing-edit-dialog">
        <DialogHeader><DialogTitle>{row._new ? 'Add Country' : `Edit ${row.country_name}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">{fld('country_code', 'Country code', 'US')}{fld('country_name', 'Country name', 'United States')}</div>
          <div className="grid grid-cols-2 gap-3">{fld('currency', 'Currency', 'USD')}{fld('symbol', 'Symbol', 'US$')}</div>
          <div className="label-mini pt-1">Transform 180 (6 months)</div>
          <div className="grid grid-cols-2 gap-3">{fld('t180_regular', 'Regular price')}{fld('t180_promo', 'Promo price')}</div>
          <div className="label-mini pt-1">Transform 365 (12 months)</div>
          <div className="grid grid-cols-2 gap-3">{fld('t365_regular', 'Regular price')}{fld('t365_promo', 'Promo price')}</div>
          <div className="flex items-center gap-3 rounded-xl bg-[var(--c-film)] p-3"><div className="flex-1 text-sm">Promotion enabled</div><Switch checked={!!f.promo_enabled} onCheckedChange={v => set('promo_enabled', v)} data-testid="cp-promo" /></div>
          <div className="flex items-center gap-3 rounded-xl bg-[var(--c-film)] p-3"><div className="flex-1 text-sm">Active (shown to users)</div><Switch checked={!!f.active} onCheckedChange={v => set('active', v)} data-testid="cp-active" /></div>
          <Button onClick={save} disabled={busy} className="w-full bg-[#00C8FF] text-[#081726] font-semibold" data-testid="cp-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
